import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import { useAuth } from "../../auth";
import {
    AgendaStatusFilters,
    Center,
    CenterAppointment,
    CenterInvitation,
    ToolPolicyState,
    CenterInvitationInput,
    CenterInvitationPreview,
    CenterMember,
    CenterMembershipView,
    CenterPaymentSettingsInput,
    CenterRole,
    CenterUpdateInput,
    CourseInput,
    StudentInput,
    StudentUpdate,
    MemberHandoverInput,
    ReassignmentPreviewInput,
    RecordCenterConsentInput,
    ReportGranularity,
} from "../domain/Center";
import type {
    PatientCustomFieldInput,
    PatientCustomFieldUpdate,
    PatientCustomValuesInput,
} from "../domain/PatientCustomField";
import {
    CenterAPI,
    CenterCourseAPI,
    CenterPatientFieldAPI,
    PatientCustomValuesAPI,
    CenterStudentAPI,
    CenterDirectoryAPI,
    CenterInvitationAPI,
} from "../infrastructure/api/center.api";

export const CENTER_ME_KEY = "center-me";
export const CENTER_MEMBERS_KEY = "center-members";

export interface CenterState {
    loading: boolean;
    /** The caller's centre and roles, or null when they work alone. */
    membership: CenterMembershipView | null;
    center: Center | null;
    isAdmin: boolean;
    isPractisingTherapist: boolean;
}

function isNotFound(error: unknown): boolean {
    return axios.isAxiosError(error) && error.response?.status === 404;
}

/**
 * The caller's centre, or the absence of one.
 *
 * A 404 resolves to `null` rather than an error: every account that exists
 * today has no centre, and a therapist who works alone is not a failed lookup.
 * Callers branch on `membership === null`, never on an error state.
 */
export const useCenter = (): CenterState => {
    const { isAuthenticated } = useAuth();

    const { data, isLoading } = useQuery({
        queryKey: [CENTER_ME_KEY],
        queryFn: async (): Promise<CenterMembershipView | null> => {
            try {
                return (await CenterAPI.me()).data;
            } catch (error) {
                if (isNotFound(error)) return null;
                throw error;
            }
        },
        enabled: isAuthenticated,
        retry: false,
    });

    const membership = data ?? null;

    return {
        loading: isAuthenticated && isLoading,
        membership,
        center: membership?.center ?? null,
        // Fails closed: anything other than a confirmed administrator is not
        // one, including an unfinished or failed lookup.
        isAdmin: membership?.is_admin === true,
        isPractisingTherapist: membership?.is_practising_therapist === true,
    };
};

export interface CenterMembersState {
    loading: boolean;
    members: CenterMember[];
}

/**
 * The centre roster.
 *
 * `practisingOnly` is the list a therapist picker or an assignment target must
 * use — it excludes administrators who do not treat, and suspended members.
 */
export const useCenterMembers = (
    centerId: string | undefined,
    practisingOnly = false
): CenterMembersState => {
    const { data, isLoading } = useQuery({
        queryKey: [CENTER_MEMBERS_KEY, centerId, practisingOnly],
        queryFn: async () => (await CenterAPI.members(centerId as string, { practisingOnly })).data,
        enabled: Boolean(centerId),
        retry: false,
    });

    return { loading: Boolean(centerId) && isLoading, members: data ?? [] };
};

export const CENTER_MEMBER_CASELOAD_KEY = "center-member-caseload";

/** What a member treats in the centre, as it must be handed over before they leave. */
export const useMemberCaseload = (
    centerId: string | undefined,
    membershipId: string | undefined
) => {
    const { data, isLoading, isError } = useQuery({
        queryKey: [CENTER_MEMBER_CASELOAD_KEY, centerId, membershipId],
        queryFn: async () =>
            (await CenterAPI.memberCaseload(centerId as string, membershipId as string)).data,
        enabled: Boolean(centerId && membershipId),
        retry: false,
    });

    return {
        loading: Boolean(centerId && membershipId) && isLoading,
        failed: isError,
        caseload: data ?? null,
    };
};

/** Hand over a member's caseload and take them out of the centre. */
export const useHandOverAndRemove = (centerId: string | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: { membershipId: string; handover: MemberHandoverInput }) => {
            await CenterAPI.handOverAndRemove(
                centerId as string,
                input.membershipId,
                input.handover
            );
        },
        // Settled: a refusal can follow a partial change on the server's side
        // (somebody brought in mid-way), and every list it touches may be stale.
        onSettled: () => {
            for (const key of [
                CENTER_MEMBERS_KEY,
                CENTER_SEATS_KEY,
                CENTER_MEMBER_CASELOAD_KEY,
                CENTER_PATIENTS_KEY,
                CENTER_AGENDA_KEY,
            ]) {
                queryClient.invalidateQueries({ queryKey: [key, centerId] });
            }
        },
    });
};

/** Change what a member does in the centre, keeping them in it. */
export const useChangeMemberRoles = (centerId: string | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: { membershipId: string; roles: CenterRole[] }) =>
            (await CenterAPI.changeMemberRoles(centerId as string, input.membershipId, input.roles))
                .data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CENTER_MEMBERS_KEY, centerId] });
        },
    });
};

/** Hand over what a member treats, then change their roles. They stay. */
export const useHandOverAndChangeRoles = (centerId: string | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: {
            membershipId: string;
            handover: MemberHandoverInput;
            roles: CenterRole[];
        }) =>
            (
                await CenterAPI.handOverAndChangeRoles(centerId as string, input.membershipId, {
                    ...input.handover,
                    roles: input.roles,
                })
            ).data,
        // Settled, like a departure: a refused role change can follow a
        // handover that did happen.
        onSettled: () => {
            for (const key of [
                CENTER_MEMBERS_KEY,
                CENTER_MEMBER_CASELOAD_KEY,
                CENTER_PATIENTS_KEY,
                CENTER_AGENDA_KEY,
            ]) {
                queryClient.invalidateQueries({ queryKey: [key, centerId] });
            }
        },
    });
};

/** Edit the centre's own details. Administrators only, enforced server-side. */
export const useUpdateCenter = (centerId: string | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: CenterUpdateInput) =>
            (await CenterAPI.update(centerId as string, payload)).data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CENTER_ME_KEY] });
        },
    });
};

/** Upload or remove the centre's logo; the centre is re-read either way. */
export const useCenterLogo = (centerId: string | undefined) => {
    const queryClient = useQueryClient();
    const refresh = () => queryClient.invalidateQueries({ queryKey: [CENTER_ME_KEY] });

    const upload = useMutation({
        mutationFn: async (file: File) =>
            (await CenterAPI.uploadLogo(centerId as string, file)).data,
        onSuccess: refresh,
    });
    const remove = useMutation({
        mutationFn: async () => (await CenterAPI.removeLogo(centerId as string)).data,
        onSuccess: refresh,
    });

    return { upload, remove };
};

export const CENTER_INVITATIONS_KEY = "center-invitations";

export interface CenterInvitationsState {
    loading: boolean;
    invitations: CenterInvitation[];
}

/** Every invitation of the centre, live or spent, newest first. */
export const useCenterInvitations = (centerId: string | undefined): CenterInvitationsState => {
    const { data, isLoading } = useQuery({
        queryKey: [CENTER_INVITATIONS_KEY, centerId],
        queryFn: async () => (await CenterInvitationAPI.list(centerId as string)).data,
        enabled: Boolean(centerId),
        retry: false,
    });

    return { loading: Boolean(centerId) && isLoading, invitations: data ?? [] };
};

export const CENTER_SEATS_KEY = "center-seats";

/** Seats the centre has and how many are taken or promised. Admins only. */
export const useCenterSeats = (centerId: string | undefined) => {
    const { data, isLoading } = useQuery({
        queryKey: [CENTER_SEATS_KEY, centerId],
        queryFn: async () => (await CenterInvitationAPI.seats(centerId as string)).data,
        enabled: Boolean(centerId),
        retry: false,
    });

    return { loading: Boolean(centerId) && isLoading, seats: data ?? null };
};

export const useInviteMember = (centerId: string | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: CenterInvitationInput) =>
            (await CenterInvitationAPI.invite(centerId as string, payload)).data,
        // Settled rather than success: a refusal for want of seats means the
        // count on screen was stale, and it should be refreshed either way.
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [CENTER_INVITATIONS_KEY, centerId] });
            queryClient.invalidateQueries({ queryKey: [CENTER_SEATS_KEY, centerId] });
        },
    });
};

export const useRevokeInvitation = (centerId: string | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (invitationId: string) => {
            await CenterInvitationAPI.revoke(centerId as string, invitationId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CENTER_INVITATIONS_KEY, centerId] });
            // A revoked invitation gives its seat back.
            queryClient.invalidateQueries({ queryKey: [CENTER_SEATS_KEY, centerId] });
        },
    });
};

export interface InvitationPreviewState {
    loading: boolean;
    preview: CenterInvitationPreview | null;
    /** True when the token itself is unknown, as opposed to spent or expired. */
    notFound: boolean;
}

/**
 * What a join link is for.
 *
 * A dead invitation resolves to a preview describing how it died, not to an
 * error: somebody holding an expired link needs to be told it expired, or they
 * will keep clicking it.
 */
export const useInvitationPreview = (token: string | undefined): InvitationPreviewState => {
    const { data, isLoading, error } = useQuery({
        queryKey: ["center-invitation-preview", token],
        queryFn: async () => (await CenterInvitationAPI.preview(token as string)).data,
        enabled: Boolean(token),
        retry: false,
    });

    return {
        loading: Boolean(token) && isLoading,
        preview: data ?? null,
        notFound: isNotFound(error),
    };
};

export const CENTER_PATIENTS_KEY = "center-patients";
export const CENTER_AGENDA_KEY = "center-agenda";
export const CENTER_REPORTS_KEY = "center-reports";
export const CENTER_PAYMENTS_KEY = "center-payments";
export const CENTER_TOOL_POLICY_KEY = "center-tool-policy";
export const CENTER_PENDING_CONSENT_KEY = "center-pending-consent";

export const useCenterPatients = (
    centerId: string | undefined,
    params: {
        therapist_id?: string;
        search?: string;
        course_id?: string;
        academic_year?: number;
        group_id?: string;
        couple_id?: string;
        limit?: number;
    } = {}
) => {
    const { data, isLoading } = useQuery({
        queryKey: [
            CENTER_PATIENTS_KEY,
            centerId,
            params.therapist_id,
            params.search,
            params.course_id,
            params.academic_year,
            params.group_id,
            params.couple_id,
            params.limit,
        ],
        queryFn: async () => (await CenterDirectoryAPI.patients(centerId as string, params)).data,
        enabled: Boolean(centerId),
        retry: false,
    });

    return {
        loading: Boolean(centerId) && isLoading,
        patients: data?.items ?? [],
        total: data?.total ?? 0,
    };
};

export const CENTER_UNITS_KEY = "center-units";

/** The caseload's couples and groups, beside its patients. */
export const useCenterUnits = (
    centerId: string | undefined,
    params: { therapist_id?: string } = {},
    enabled = true
) => {
    const { data, isLoading } = useQuery({
        queryKey: [CENTER_UNITS_KEY, centerId, params.therapist_id],
        queryFn: async () => (await CenterDirectoryAPI.units(centerId as string, params)).data,
        enabled: Boolean(centerId) && enabled,
        retry: false,
    });
    return {
        loading: Boolean(centerId) && enabled && isLoading,
        couples: data?.couples ?? [],
        groups: data?.groups ?? [],
    };
};

export const CENTER_REASSIGNMENT_PREVIEW_KEY = "center-reassignment-preview";

/**
 * What handing somebody to a chosen therapist would do to their bookings
 * still to come. Asked once a destination is picked; `null` asks nothing.
 *
 * A failed preview is not a failed handover: the screen shows no warnings
 * and the handover itself answers for whatever it refuses.
 */
export const useReassignmentPreview = (
    centerId: string | undefined,
    input: ReassignmentPreviewInput | null
) => {
    const enabled = Boolean(centerId && input);
    const { data, isLoading } = useQuery({
        queryKey: [
            CENTER_REASSIGNMENT_PREVIEW_KEY,
            centerId,
            input?.kind,
            input?.id,
            input?.toTherapistId,
        ],
        queryFn: async () =>
            (
                await CenterDirectoryAPI.reassignmentPreview(
                    centerId as string,
                    input as ReassignmentPreviewInput
                )
            ).data,
        enabled,
        retry: false,
    });
    return { loading: enabled && isLoading, preview: enabled ? data ?? null : null };
};

/** Hand a couple or a group over whole; its members move with it. */
export const useReassignUnit = (centerId: string | undefined) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            kind: "couple" | "group";
            unitId: string;
            toTherapistId: string;
            reason?: string;
        }) =>
            (
                await CenterDirectoryAPI.reassignUnit(centerId as string, input.kind, input.unitId, {
                    to_therapist_id: input.toTherapistId,
                    reason: input.reason,
                })
            ).data,
        onSuccess: () => {
            // Its members and their bookings moved with it.
            queryClient.invalidateQueries({ queryKey: [CENTER_UNITS_KEY, centerId] });
            queryClient.invalidateQueries({ queryKey: [CENTER_PATIENTS_KEY, centerId] });
            queryClient.invalidateQueries({ queryKey: [CENTER_AGENDA_KEY, centerId] });
            queryClient.invalidateQueries({ queryKey: [CENTER_REASSIGNMENT_PREVIEW_KEY, centerId] });
        },
    });
};

export const useReassignPatient = (centerId: string | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: { patientId: string; toTherapistId: string; reason?: string }) =>
            (
                await CenterDirectoryAPI.reassign(centerId as string, input.patientId, {
                    to_therapist_id: input.toTherapistId,
                    reason: input.reason,
                })
            ).data,
        onSuccess: () => {
            // The agenda moves with the patient, so it is stale too.
            queryClient.invalidateQueries({ queryKey: [CENTER_PATIENTS_KEY, centerId] });
            queryClient.invalidateQueries({ queryKey: [CENTER_UNITS_KEY, centerId] });
            queryClient.invalidateQueries({ queryKey: [CENTER_AGENDA_KEY, centerId] });
            queryClient.invalidateQueries({ queryKey: [CENTER_REASSIGNMENT_PREVIEW_KEY, centerId] });
        },
    });
};

export const useCenterAgenda = (
    centerId: string | undefined,
    range: { start: string; end: string },
    therapistIds?: string[],
    statuses: AgendaStatusFilters = {},
    // `enabled` false while the calendar, which reads its own range, is on screen.
    page: { patientIds?: string[]; offset?: number; limit?: number; enabled?: boolean } = {}
) => {
    const { data, isLoading } = useQuery({
        queryKey: [
            CENTER_AGENDA_KEY,
            centerId,
            range.start,
            range.end,
            therapistIds?.join(","),
            statuses.sessionStatus,
            statuses.paymentStatus,
            statuses.scheduledInAlchely,
            page.patientIds?.join(","),
            page.offset,
            page.limit,
        ],
        queryFn: async () =>
            (
                await CenterDirectoryAPI.agenda(centerId as string, {
                    start: range.start,
                    end: range.end,
                    therapist_id: therapistIds?.length ? therapistIds : undefined,
                    session_status: statuses.sessionStatus,
                    payment_status: statuses.paymentStatus,
                    scheduled_in_alchely: statuses.scheduledInAlchely,
                    patient_id: page.patientIds?.length ? page.patientIds : undefined,
                    offset: page.offset,
                    limit: page.limit,
                })
            ).data,
        enabled: Boolean(centerId) && (page.enabled ?? true),
        retry: false,
        // The page on screen stays while the next one loads, rather than the
        // table collapsing to "loading" on every step.
        placeholderData: keepPreviousData,
    });

    return {
        loading: Boolean(centerId) && isLoading,
        appointments: data?.items ?? [],
        total: data?.total ?? 0,
    };
};

/** The most rows the agenda endpoint returns in one page. */
const AGENDA_MAX_PAGE = 200;

/**
 * Every booking in a range, for the calendar views.
 *
 * A calendar cannot paginate — a week with one session missing reads as a
 * free hour — so this walks the endpoint's pages until it has them all.
 */
export const useCenterAgendaCalendar = (
    centerId: string | undefined,
    range: { start: string; end: string },
    therapistIds?: string[],
    statuses: AgendaStatusFilters = {},
    patientIds?: string[],
    enabled = true
) => {
    const { data, isLoading } = useQuery({
        queryKey: [
            CENTER_AGENDA_KEY,
            "calendar",
            centerId,
            range.start,
            range.end,
            therapistIds?.join(","),
            statuses.sessionStatus,
            statuses.paymentStatus,
            statuses.scheduledInAlchely,
            patientIds?.join(","),
        ],
        queryFn: async () => {
            const items: CenterAppointment[] = [];
            let total = Number.POSITIVE_INFINITY;
            while (items.length < total) {
                const page = (
                    await CenterDirectoryAPI.agenda(centerId as string, {
                        start: range.start,
                        end: range.end,
                        therapist_id: therapistIds?.length ? therapistIds : undefined,
                        session_status: statuses.sessionStatus,
                        payment_status: statuses.paymentStatus,
                        scheduled_in_alchely: statuses.scheduledInAlchely,
                        patient_id: patientIds?.length ? patientIds : undefined,
                        offset: items.length,
                        limit: AGENDA_MAX_PAGE,
                    })
                ).data;
                items.push(...page.items);
                total = page.total;
                // A short page means the rows ran out, whatever the total said.
                if (page.items.length < AGENDA_MAX_PAGE) break;
            }
            return items;
        },
        enabled: Boolean(centerId) && enabled,
        retry: false,
        // The week on screen stays while the next one loads.
        placeholderData: keepPreviousData,
    });

    return {
        loading: Boolean(centerId) && enabled && isLoading,
        appointments: data ?? [],
    };
};

export const useMoveAppointment = (centerId: string | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: { appointmentId: string; toTherapistId: string }) =>
            (
                await CenterDirectoryAPI.moveAppointment(centerId as string, input.appointmentId, {
                    to_therapist_id: input.toTherapistId,
                })
            ).data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CENTER_AGENDA_KEY, centerId] });
        },
    });
};

/**
 * Work per professional. With a therapist, only theirs — counted, like every
 * report, for whoever held the patient when the work happened.
 */
export const useCenterReports = (
    centerId: string | undefined,
    range: { start: string; end: string },
    therapistId?: string
) => {
    const params = { ...range, therapist_id: therapistId || undefined };
    const sessions = useQuery({
        queryKey: [CENTER_REPORTS_KEY, "sessions", centerId, range.start, range.end, therapistId],
        queryFn: async () =>
            (await CenterDirectoryAPI.sessionReport(centerId as string, params)).data,
        enabled: Boolean(centerId),
        retry: false,
    });
    const agenda = useQuery({
        queryKey: [CENTER_REPORTS_KEY, "agenda", centerId, range.start, range.end, therapistId],
        queryFn: async () =>
            (await CenterDirectoryAPI.agendaReport(centerId as string, params)).data,
        enabled: Boolean(centerId),
        retry: false,
    });

    return {
        loading: Boolean(centerId) && (sessions.isLoading || agenda.isLoading),
        sessions: sessions.data ?? [],
        agenda: agenda.data ?? [],
    };
};

/** A 422 here means the window holds more periods than one chart can show. */
const isTooManyPeriods = (error: unknown) =>
    axios.isAxiosError(error) &&
    error.response?.status === 422 &&
    error.response?.data?.detail === "too_many_periods";

/**
 * The reports over time: work and money per day, month or year.
 *
 * `tooManyPeriods` is the one refusal a person can act on — two years by day
 * is too many bars — so it is told apart from any other failure.
 */
export const useCenterTimeReports = (
    centerId: string | undefined,
    range: { start: string; end: string },
    granularity: ReportGranularity,
    therapistId?: string
) => {
    const params = { ...range, granularity, therapist_id: therapistId || undefined };
    const activity = useQuery({
        queryKey: [
            CENTER_REPORTS_KEY,
            "activity",
            centerId,
            range.start,
            range.end,
            granularity,
            therapistId,
        ],
        queryFn: async () =>
            (await CenterDirectoryAPI.activityReport(centerId as string, params)).data,
        enabled: Boolean(centerId),
        retry: false,
    });
    const payments = useQuery({
        queryKey: [
            CENTER_REPORTS_KEY,
            "payments",
            centerId,
            range.start,
            range.end,
            granularity,
            therapistId,
        ],
        queryFn: async () =>
            (await CenterDirectoryAPI.paymentReport(centerId as string, params)).data,
        enabled: Boolean(centerId),
        retry: false,
    });

    return {
        loading: Boolean(centerId) && (activity.isLoading || payments.isLoading),
        activity: activity.data ?? [],
        paymentPeriods: payments.data?.periods ?? [],
        paymentStatuses: payments.data?.statuses ?? [],
        tooManyPeriods: isTooManyPeriods(activity.error) || isTooManyPeriods(payments.error),
    };
};

export const useCenterPayments = (
    centerId: string | undefined,
    params: { therapist_id?: string; payment_status?: string } = {}
) => {
    const { data, isLoading } = useQuery({
        queryKey: [CENTER_PAYMENTS_KEY, centerId, params.therapist_id, params.payment_status],
        queryFn: async () => (await CenterDirectoryAPI.payments(centerId as string, params)).data,
        enabled: Boolean(centerId),
        retry: false,
    });

    return {
        loading: Boolean(centerId) && isLoading,
        payments: data?.items ?? [],
        total: data?.total ?? 0,
    };
};

export const useCenterToolPolicy = (centerId: string | undefined) => {
    const { data, isLoading } = useQuery({
        queryKey: [CENTER_TOOL_POLICY_KEY, centerId],
        queryFn: async () => (await CenterDirectoryAPI.toolPolicy(centerId as string)).data,
        enabled: Boolean(centerId),
        retry: false,
    });

    return { loading: Boolean(centerId) && isLoading, policies: data ?? [] };
};

/**
 * Whether the centre has payments switched on — the payments tool in its tool
 * policy. Off while unknown, so a screen does not show money and then take
 * it away; off too when the catalogue has no payments tool at all.
 */
export const useCenterPaymentsEnabled = (centerId: string | undefined) => {
    const { loading, policies } = useCenterToolPolicy(centerId);
    const payments = policies.find((policy) => policy.slug === "payments");
    return { loading, enabled: payments?.state === "enabled" };
};

export const useSetToolPolicy = (centerId: string | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: { toolId: string; state: ToolPolicyState }) =>
            (await CenterDirectoryAPI.setToolPolicy(centerId as string, input.toolId, input.state))
                .data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CENTER_TOOL_POLICY_KEY, centerId] });
        },
    });
};

/**
 * The caller's own patients whose records the centre does not hold.
 *
 * Returns an empty list for a therapist with no centre — there is nobody to
 * bring anywhere, and that is not an error.
 */
export const usePendingCenterConsent = () => {
    const { membership } = useCenter();
    const queryClient = useQueryClient();

    const { data, isLoading } = useQuery({
        queryKey: [CENTER_PENDING_CONSENT_KEY],
        queryFn: async () => (await CenterDirectoryAPI.pendingConsent()).data,
        enabled: Boolean(membership),
        retry: false,
    });

    const record = useMutation({
        mutationFn: async ({ patientId, withGroup = false }: RecordCenterConsentInput) =>
            (await CenterDirectoryAPI.recordConsent(patientId, withGroup)).data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CENTER_PENDING_CONSENT_KEY] });
            queryClient.invalidateQueries({ queryKey: [CENTER_PATIENTS_KEY] });
        },
    });

    return {
        loading: Boolean(membership) && isLoading,
        patients: data ?? [],
        recordConsent: record,
    };
};

export const CENTER_PAYMENT_SETTINGS_KEY = "center-payment-settings";

/** How the centre charges for its patients: its Stripe account and defaults. */
export const useCenterPaymentSettings = (centerId: string | undefined) => {
    const { data, isLoading } = useQuery({
        queryKey: [CENTER_PAYMENT_SETTINGS_KEY, centerId],
        queryFn: async () => (await CenterDirectoryAPI.paymentSettings(centerId as string)).data,
        enabled: Boolean(centerId),
        retry: false,
    });
    return { loading: Boolean(centerId) && isLoading, settings: data ?? null };
};

export const useUpdateCenterPaymentSettings = (centerId: string | undefined) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: CenterPaymentSettingsInput) =>
            (await CenterDirectoryAPI.updatePaymentSettings(centerId as string, payload)).data,
        onSuccess: (settings) => {
            queryClient.setQueryData([CENTER_PAYMENT_SETTINGS_KEY, centerId], settings);
        },
    });
};

/**
 * Start linking the centre's Stripe account. Like a therapist's: fetch the
 * consent URL with the session, then send the whole page there.
 */
export const useStartCenterStripeConnect = (centerId: string | undefined) =>
    useMutation({
        mutationFn: async () => {
            const { data } = await CenterDirectoryAPI.stripeAuthorizeUrl(centerId as string);
            window.location.assign(data.url);
        },
    });

export const useDisconnectCenterStripe = (centerId: string | undefined) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            await CenterDirectoryAPI.disconnectStripe(centerId as string);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CENTER_PAYMENT_SETTINGS_KEY, centerId] });
        },
    });
};

export const CENTER_COURSES_KEY = "center-courses";

/** A training centre's courses, with how many students each has. */
export const useCenterCourses = (centerId: string | undefined, enabled = true) => {
    const { data, isLoading, isError } = useQuery({
        queryKey: [CENTER_COURSES_KEY, centerId],
        queryFn: async () => (await CenterCourseAPI.list(centerId as string)).data,
        enabled: enabled && Boolean(centerId),
        retry: false,
    });
    return { courses: data ?? [], loading: Boolean(centerId) && enabled && isLoading, failed: isError };
};

const useCourseWrite = <TVariables, TData>(
    centerId: string | undefined,
    write: (variables: TVariables) => Promise<TData>
) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: write,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CENTER_COURSES_KEY, centerId] });
        },
    });
};

export const useCreateCourse = (centerId: string | undefined) =>
    useCourseWrite(centerId, async (course: CourseInput) =>
        (await CenterCourseAPI.create(centerId as string, course)).data
    );

export const useUpdateCourse = (centerId: string | undefined) =>
    useCourseWrite(centerId, async (input: { courseId: string; changes: CourseInput }) =>
        (await CenterCourseAPI.update(centerId as string, input.courseId, input.changes)).data
    );

export const useDeleteCourse = (centerId: string | undefined) =>
    useCourseWrite(centerId, async (courseId: string) => {
        await CenterCourseAPI.remove(centerId as string, courseId);
    });

export const useCreateCourses = (centerId: string | undefined) =>
    useCourseWrite(centerId, async (courses: CourseInput[]) =>
        (await CenterCourseAPI.createMany(centerId as string, courses)).data.results
    );

/** Invite a list of people at once; the results say which went out and why not. */
export const useInviteMembers = (centerId: string | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: { emails: string[]; language: "es" | "en" }) =>
            (await CenterInvitationAPI.inviteMany(centerId as string, payload)).data.results,
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [CENTER_INVITATIONS_KEY, centerId] });
            queryClient.invalidateQueries({ queryKey: [CENTER_SEATS_KEY, centerId] });
        },
    });
};

/** Take students on for a training centre; the results say, per row, what became of each. */
export const useEnrollStudents = (centerId: string | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (students: StudentInput[]) =>
            (await CenterStudentAPI.enrollMany(centerId as string, students)).data.results,
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [CENTER_PATIENTS_KEY, centerId] });
            queryClient.invalidateQueries({ queryKey: [CENTER_UNITS_KEY, centerId] });
            queryClient.invalidateQueries({ queryKey: [CENTER_COURSES_KEY, centerId] });
        },
    });
};

export const CENTER_PATIENT_DETAIL_KEY = "center-patient-detail";

/** One patient of the centre, when one is opened; nothing is read until then. */
export const useCenterPatientDetail = (centerId: string | undefined, patientId: string | null) => {
    const { data, isLoading, isError } = useQuery({
        queryKey: [CENTER_PATIENT_DETAIL_KEY, centerId, patientId],
        queryFn: async () =>
            (await CenterDirectoryAPI.patient(centerId as string, patientId as string)).data,
        enabled: Boolean(centerId && patientId),
        retry: false,
    });
    return { detail: data ?? null, loading: Boolean(patientId) && isLoading, failed: isError };
};

/** Correct a training centre's student; the record and the list are read again. */
export const useUpdateStudent = (centerId: string | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: { patientId: string; changes: StudentUpdate }) =>
            (await CenterDirectoryAPI.updateStudent(centerId as string, input.patientId, input.changes)).data,
        onSuccess: (detail) => {
            queryClient.setQueryData([CENTER_PATIENT_DETAIL_KEY, centerId, detail.patient_id], detail);
            queryClient.invalidateQueries({ queryKey: [CENTER_PATIENTS_KEY, centerId] });
            queryClient.invalidateQueries({ queryKey: [CENTER_COURSES_KEY, centerId] });
        },
    });
};

/**
 * Erase a patient or student of the centre. Everything that counted them —
 * the list, the couples and groups, the courses — is read again.
 */
export const useEraseCenterPatient = (centerId: string | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (patientId: string) => {
            await CenterDirectoryAPI.erasePatient(centerId as string, patientId);
        },
        onSuccess: (_result, patientId) => {
            queryClient.removeQueries({ queryKey: [CENTER_PATIENT_DETAIL_KEY, centerId, patientId] });
            queryClient.invalidateQueries({ queryKey: [CENTER_PATIENTS_KEY, centerId] });
            queryClient.invalidateQueries({ queryKey: [CENTER_UNITS_KEY, centerId] });
            queryClient.invalidateQueries({ queryKey: [CENTER_COURSES_KEY, centerId] });
        },
    });
};

/** Group students the centre already has under one of its mentors. */
export const useCreateCenterGroup = (centerId: string | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: { therapist_id: string; patient_ids: string[]; display_name: string | null }) =>
            (await CenterDirectoryAPI.createGroup(centerId as string, input)).data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CENTER_UNITS_KEY, centerId] });
            queryClient.invalidateQueries({ queryKey: [CENTER_PATIENTS_KEY, centerId] });
        },
    });
};

/** Rename one of a training centre's mentor groups, or change who is in it. */
export const useEditCenterGroup = (centerId: string | undefined) => {
    const queryClient = useQueryClient();
    const refresh = () => {
        queryClient.invalidateQueries({ queryKey: [CENTER_UNITS_KEY, centerId] });
        queryClient.invalidateQueries({ queryKey: [CENTER_PATIENTS_KEY, centerId] });
    };
    const rename = useMutation({
        mutationFn: async (input: { groupId: string; displayName: string | null }) => {
            await CenterDirectoryAPI.renameGroup(centerId as string, input.groupId, input.displayName);
        },
        onSuccess: refresh,
    });
    const add = useMutation({
        mutationFn: async (input: { groupId: string; patientId: string }) => {
            await CenterDirectoryAPI.addGroupMember(centerId as string, input.groupId, input.patientId);
        },
        onSuccess: refresh,
    });
    const remove = useMutation({
        mutationFn: async (input: { groupId: string; patientId: string }) => {
            await CenterDirectoryAPI.removeGroupMember(centerId as string, input.groupId, input.patientId);
        },
        onSuccess: refresh,
    });
    return { rename, add, remove };
};

/** A couple of two patients the centre already has, of one of its therapists. */
export const useCreateCenterCouple = (centerId: string | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: { therapist_id: string; patient_ids: string[]; display_name: string | null }) =>
            (await CenterDirectoryAPI.createCouple(centerId as string, input)).data,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CENTER_UNITS_KEY, centerId] });
            queryClient.invalidateQueries({ queryKey: [CENTER_PATIENTS_KEY, centerId] });
        },
    });
};

/** Name one of the centre's couples; its partners are the couple and are not edited. */
export const useRenameCenterCouple = (centerId: string | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: { coupleId: string; displayName: string | null }) => {
            await CenterDirectoryAPI.renameCouple(centerId as string, input.coupleId, input.displayName);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CENTER_UNITS_KEY, centerId] });
        },
    });
};

/** One partner of a couple out and another of the therapist's patients in, before it has met. */
export const useReplaceCouplePartner = (centerId: string | undefined) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: { coupleId: string; oldPatientId: string; newPatientId: string }) => {
            await CenterDirectoryAPI.replaceCouplePartner(
                centerId as string,
                input.coupleId,
                input.oldPatientId,
                input.newPatientId
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CENTER_UNITS_KEY, centerId] });
            queryClient.invalidateQueries({ queryKey: [CENTER_PATIENTS_KEY, centerId] });
        },
    });
};

export const CENTER_PATIENT_FIELDS_KEY = "center-patient-fields";
export const CENTER_PATIENT_CUSTOM_VALUES_KEY = "center-patient-custom-values";
export const PATIENT_CUSTOM_VALUES_KEY = "patient-custom-values";

/** The centre's own fields for its patients, in their order. */
export const useCenterPatientFields = (centerId: string | undefined) => {
    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: [CENTER_PATIENT_FIELDS_KEY, centerId],
        queryFn: async () => (await CenterPatientFieldAPI.list(centerId as string)).data.fields,
        enabled: Boolean(centerId),
        retry: false,
    });
    return {
        fields: data ?? [],
        loading: Boolean(centerId) && isLoading,
        failed: isError,
        retry: () => void refetch(),
    };
};

/**
 * A change to the definitions changes what every person's values read as —
 * a label, a field gone with its values — so those are read again too.
 */
const usePatientFieldWrite = <TVariables, TData>(
    centerId: string | undefined,
    write: (variables: TVariables) => Promise<TData>
) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: write,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [CENTER_PATIENT_FIELDS_KEY, centerId] });
            queryClient.invalidateQueries({ queryKey: [CENTER_PATIENT_CUSTOM_VALUES_KEY, centerId] });
            queryClient.invalidateQueries({ queryKey: [PATIENT_CUSTOM_VALUES_KEY] });
        },
    });
};

export const useCreatePatientField = (centerId: string | undefined) =>
    usePatientFieldWrite(centerId, async (field: PatientCustomFieldInput) =>
        (await CenterPatientFieldAPI.create(centerId as string, field)).data
    );

export const useUpdatePatientField = (centerId: string | undefined) =>
    usePatientFieldWrite(centerId, async (input: { fieldId: string; changes: PatientCustomFieldUpdate }) =>
        (await CenterPatientFieldAPI.update(centerId as string, input.fieldId, input.changes)).data
    );

export const useDeletePatientField = (centerId: string | undefined) =>
    usePatientFieldWrite(centerId, async (fieldId: string) => {
        await CenterPatientFieldAPI.remove(centerId as string, fieldId);
    });

/** Every field's id in the new order; the list on screen takes the answer at once. */
export const useReorderPatientFields = (centerId: string | undefined) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (fieldIds: string[]) =>
            (await CenterPatientFieldAPI.reorder(centerId as string, fieldIds)).data.fields,
        onSuccess: (fields) => {
            queryClient.setQueryData([CENTER_PATIENT_FIELDS_KEY, centerId], fields);
            queryClient.invalidateQueries({ queryKey: [CENTER_PATIENT_CUSTOM_VALUES_KEY, centerId] });
            queryClient.invalidateQueries({ queryKey: [PATIENT_CUSTOM_VALUES_KEY] });
        },
    });
};

/** One held patient's values, for the centre's administrator; nothing is read until one is opened. */
export const useCenterPatientCustomValues = (centerId: string | undefined, patientId: string | null | undefined) => {
    const enabled = Boolean(centerId && patientId);
    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: [CENTER_PATIENT_CUSTOM_VALUES_KEY, centerId, patientId],
        queryFn: async () =>
            (await CenterPatientFieldAPI.values(centerId as string, patientId as string)).data,
        enabled,
        retry: false,
    });
    return { data, loading: enabled && isLoading, failed: isError, retry: () => void refetch() };
};

export const useSaveCenterPatientCustomValues = (
    centerId: string | undefined,
    patientId: string | null | undefined
) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (values: PatientCustomValuesInput) =>
            (await CenterPatientFieldAPI.saveValues(centerId as string, patientId as string, values)).data,
        onSuccess: (saved) => {
            queryClient.setQueryData([CENTER_PATIENT_CUSTOM_VALUES_KEY, centerId, patientId], saved);
            queryClient.invalidateQueries({ queryKey: [PATIENT_CUSTOM_VALUES_KEY, patientId] });
        },
    });
};

/**
 * A patient's values, for the professional who owns them. `enabled` false for
 * a patient no centre holds: there is nothing to ask for.
 */
export const usePatientCustomValues = (patientId: string | null | undefined, enabled = true) => {
    const active = enabled && Boolean(patientId);
    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: [PATIENT_CUSTOM_VALUES_KEY, patientId],
        queryFn: async () => (await PatientCustomValuesAPI.get(patientId as string)).data,
        enabled: active,
        retry: false,
    });
    return { data, loading: active && isLoading, failed: isError, retry: () => void refetch() };
};

export const useSavePatientCustomValues = (patientId: string | null | undefined) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (values: PatientCustomValuesInput) =>
            (await PatientCustomValuesAPI.save(patientId as string, values)).data,
        onSuccess: (saved) => {
            queryClient.setQueryData([PATIENT_CUSTOM_VALUES_KEY, patientId], saved);
            queryClient.invalidateQueries({ queryKey: [CENTER_PATIENT_CUSTOM_VALUES_KEY] });
        },
    });
};
